import { Columns } from '../../../../widgets/columns';
import { Icon } from '../../../../widgets/icon';
import { GhostButton, Link } from '../../../../widgets/buttons';
import { ZTooltip } from '../../../../widgets/ztooltip';
import React, { FunctionComponent } from 'react';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { VerticalDivider } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import JSONIcon from 'src/shared/material_icons/editor/data_object.svg';
import EditIcon from 'src/shared/material_icons/editor/mode_edit.svg';
import styled, { keyframes } from 'styled-components';
import { getAllModelSides, useProductConfigurationState } from '../../state';
import { ModelColor } from '../../state/model/modelColor';
import { ModelSide } from '../../state/model/modelSide';
import VariantCell from './variantCell';

const Container = styled.div`
    margin-bottom: 24px;
`;

const Cells = styled(Columns)`
    grid-gap: 0;
`;

const VariantLabel = styled.h5`
    margin: 0;
`;

const VariantTitle = styled.div`
    flex: 1;
    min-width: 0;
`;

const VariantCode = styled.span`
    display: block;
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.3;
    color: #78909c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Name = styled.div`
    margin: 0;
    margin-bottom: 8px;
    display: flex;
    align-items: center;

    ${VariantLabel} {
        font-weight: ${(props) => props.theme.text.boldWeight};
        font-size: 16px;
    }

    ${Link} {
        color: ${(props) => props.theme.app.primary};
    }
`;

const VariantActionButton = styled(GhostButton).attrs({
    type: 'button',
    $size: 'small' as const,
    $type: 'icon' as const
})`
    flex-shrink: 0;
`;

const VerticalDividerStyled = styled(VerticalDivider)`
    border-left: 1px solid rgb(219, 226, 230);
    height: 24px;
    margin-right: 8px;
`;

const blinkGlow = keyframes`
    0%, 100% {
        filter: none;
    }
    50% {
        filter: drop-shadow(0 0 6px rgba(211, 47, 47, 0.8)) drop-shadow(0 0 12px rgba(211, 47, 47, 0.4));
    }
`;

const HighlightedEditButton = styled(VariantActionButton)`
    svg {
        color: #d32f2f;
    }
    animation: ${blinkGlow} 0.8s ease-in-out infinite;
`;

// List-mode row: a compact single row per variant (like the customizable products list), sides inline.
const ListRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 10px 4px;
    border-bottom: 1px solid #eceff1;
`;

const ListInfo = styled.div`
    flex: 0 0 160px;
    min-width: 0;
`;

const ListSides = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
    flex-wrap: wrap;
`;

const ListSideThumb = styled.button`
    width: 40px;
    height: 40px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    padding: 2px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;

    img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }

    &:hover {
        border-color: ${(props) => props.theme.app.primary};
    }
`;

const ListActions = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    margin-left: auto;
`;

interface VariantRowProps {
    color: ModelColor;
    isSidesPerVariantEnabled: boolean;
    showRemoveButton: boolean;
    showEditButton: boolean;
    dragHandle?: React.ReactNode;
    viewMode?: 'cards' | 'table';
    onToggleClick: (colorGuid: string, sideGuid: string) => void;
    onImageChangeClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onAiGenerateClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onAreaChangeClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onRemoveClick: (color: ModelColor) => void;
    onEditClick: (color: ModelColor) => void;
    onSetMandatorySide: (value: boolean, modelSide: ModelSide) => void;
    onSetRotateSide: (value: boolean, modelSide: ModelSide) => void;
    onClickJsonSettings: () => void;
    highlightEdit?: boolean;
}

const VariantRow: FunctionComponent<VariantRowProps> = ({
    color,
    isSidesPerVariantEnabled,
    showRemoveButton,
    showEditButton,
    dragHandle,
    viewMode,
    onImageChangeClick,
    onAiGenerateClick,
    onToggleClick,
    onAreaChangeClick,
    onRemoveClick,
    onEditClick,
    onSetMandatorySide,
    onSetRotateSide,
    onClickJsonSettings,
    highlightEdit
}) => {
    const { state } = useProductConfigurationState();
    const { userInfo } = useAccountData();
    const isJsonProductsImportFeatureEnabled = useFeatureFlagWithConditions('jsonProductsImport', {
        userId: userInfo?.userID?.toString()
    });

    const jsonSettingsIcon = isJsonProductsImportFeatureEnabled && (
        <ZTooltip
            position='top'
            title={T._(
                "Edit JSON settings - These settings are only needed for bulk import functionality to reference the entities in the import JSON. If you don't need the JSON bulk import functionality, you can ignore these parameters.",
                'Admin'
            )}
        >
            <VariantActionButton aria-label={T._('Edit JSON settings', 'Admin')} onClick={onClickJsonSettings}>
                <Icon>
                    <JSONIcon />
                </Icon>
            </VariantActionButton>
        </ZTooltip>
    );

    const editButton =
        showEditButton &&
        (highlightEdit ? (
            <HighlightedEditButton aria-label={T._('Edit variant', 'Admin')} onClick={() => onEditClick(color)}>
                <Icon>
                    <EditIcon />
                </Icon>
            </HighlightedEditButton>
        ) : (
            <VariantActionButton aria-label={T._('Edit variant', 'Admin')} onClick={() => onEditClick(color)}>
                <Icon>
                    <EditIcon />
                </Icon>
            </VariantActionButton>
        ));

    // The variant's sides, shared by grid and list modes (list uses the compact cell size).
    const sidesGrid = (compact: boolean) => (
        <Cells $xs={1} $md={5}>
            {getAllModelSides(state.product).map((x) => {
                const modelSide = color.sides.find((j) => j.originalSideGuid == x.originalSideGuid);
                const side = state.sides.find((j) => j.guid == x.originalSideGuid)!;

                return (
                    <VariantCell
                        key={side.guid}
                        side={side}
                        compact={compact}
                        isToggleEnabled={isSidesPerVariantEnabled}
                        isAreaChangeEnabled={isSidesPerVariantEnabled}
                        modelColor={color}
                        modelSide={modelSide}
                        onToggleClick={() => onToggleClick(color.guid, side.guid)}
                        onAreaChangeClick={() => onAreaChangeClick(modelSide!, color)}
                        onImageChangeClick={() => onImageChangeClick(modelSide!, color)}
                        onAiGenerateClick={() => onAiGenerateClick(modelSide!, color)}
                        onSetMandatorySide={(value) => onSetMandatorySide(value, modelSide!)}
                        onSetRotateSide={(value) => onSetRotateSide(value, modelSide!)}
                    />
                );
            })}

            {state.sides.length % 5 > 0 &&
                new Array(5 - (state.sides.length % 5)).fill(0).map((_, index) => {
                    return <VariantCell key={index} modelColor={color} modelSide={null} dummy={true} compact={compact} />;
                })}
        </Cells>
    );

    // List (table) mode: one compact row per variant (like the customizable products list) with the sides inline.
    if (viewMode === 'table') {
        return (
            <ListRow>
                {dragHandle}
                <ListInfo>
                    <VariantLabel>{color.name}</VariantLabel>
                    {color.colorProviderCode && (
                        <VariantCode>
                            {T._('Code', 'Admin')}: {color.colorProviderCode}
                        </VariantCode>
                    )}
                </ListInfo>
                <ListSides>
                    {getAllModelSides(state.product).map((x) => {
                        const modelSide = color.sides.find((j) => j.originalSideGuid == x.originalSideGuid);
                        const side = state.sides.find((j) => j.guid == x.originalSideGuid)!;
                        return (
                            <ZTooltip key={side.guid} title={side.name}>
                                <ListSideThumb type='button' onClick={() => modelSide && onImageChangeClick(modelSide, color)}>
                                    {modelSide?.imageUrl && <img src={modelSide.imageUrl} alt={side.name} />}
                                </ListSideThumb>
                            </ZTooltip>
                        );
                    })}
                </ListSides>
                <ListActions>
                    {jsonSettingsIcon}
                    {editButton}
                    {showRemoveButton && (
                        <VariantActionButton aria-label={T._('Delete variant', 'Admin')} onClick={() => onRemoveClick(color)}>
                            <Icon>
                                <DeleteIcon />
                            </Icon>
                        </VariantActionButton>
                    )}
                </ListActions>
            </ListRow>
        );
    }

    return (
        <Container>
            <Name>
                {color.colorID != 1 && (
                    <VariantTitle>
                        <VariantLabel>{color.name}</VariantLabel>
                        {color.colorProviderCode && (
                            <VariantCode>
                                {T._('Code', 'Admin')}: {color.colorProviderCode}
                            </VariantCode>
                        )}
                    </VariantTitle>
                )}

                {isJsonProductsImportFeatureEnabled && (
                    <>
                        {jsonSettingsIcon}
                        <VerticalDividerStyled />
                    </>
                )}

                {dragHandle}

                {editButton}
                {showRemoveButton && (
                    <VariantActionButton aria-label={T._('Delete variant', 'Admin')} onClick={() => onRemoveClick(color)}>
                        <Icon>
                            <DeleteIcon />
                        </Icon>
                    </VariantActionButton>
                )}
            </Name>

            {sidesGrid(false)}
        </Container>
    );
};

export default VariantRow;
